// Smarticks: Israeli-style phone-number normalisation for the dashboard.
//
// Israeli users type local numbers ("052-000-0000", "0520000000", "972520000000")
// while Contact#phone_number only accepts E.164 ("+972520000000"). These two helpers
// turn what a user typed into E.164 when libphonenumber-js can parse it, and return
// null otherwise so the caller can fall back to upstream behaviour unchanged.
import { parsePhoneNumber } from 'libphonenumber-js';

// Smarticks is an Israel-only fleet: a number typed without "+" is an Israeli number.
// (Deliberately NOT the browser timezone: an agent in Berlin would silently turn
// "052…" into a valid German number.)
export const DEFAULT_COUNTRY = 'IL';

// digits, spaces, dashes, parens, an optional leading "+"; nothing else
const PHONE_SHAPE = /^\+?[\d\s\-()]+$/;
// Israeli numbers are 9-10 digits; 7-8 digit strings (dates, ids) must not pass
const MIN_DIGITS = 9;

/**
 * Does this string look like a phone number rather than an email or a name?
 * @param {string} input
 * @returns {boolean}
 */
export const looksLikePhone = input => {
  if (typeof input !== 'string') return false;
  const value = input.trim();
  if (!PHONE_SHAPE.test(value)) return false;
  return value.replace(/\D/g, '').length >= MIN_DIGITS;
};

// formatting characters a person types inside a number
const FORMATTING = /[\s\-()]/g;

/**
 * May the compose modal create a contact from this input? Only a local MOBILE ("05" + 8
 * digits) or an explicit "+…" / "972…" form: a 9-digit Israeli ID (032123456) is also a
 * valid landline shape and must never silently become a contact.
 * @param {string} input
 * @returns {boolean}
 */
export const isComposablePhone = input => {
  if (!looksLikePhone(input)) return false;
  const compact = input.trim().replace(FORMATTING, '');
  return /^(\+|972)/.test(compact) || /^05\d{8}$/.test(compact);
};

/**
 * What to send to the contact search for this input. The backend is
 * `phone_number ILIKE '%q%'` and a "+" does not survive the wire, so a number is searched by
 * its national significant number ("052-545-7077" -> "525457077"), which is a substring of
 * "+972525457077", of a stored "+9720525457077" bug row, and of a numeric identifier.
 * @param {string} input
 * @returns {string} the search term (the trimmed input when it is not a phone)
 */
export const phoneSearchTerm = input => {
  const value = typeof input === 'string' ? input.trim() : '';
  if (!looksLikePhone(value)) return value;
  try {
    const parsed = parsePhoneNumber(value, DEFAULT_COUNTRY);
    return parsed?.nationalNumber || value;
  } catch (error) {
    return value;
  }
};

/**
 * Normalise a typed phone number to E.164.
 * A leading "+" wins over countryCode (the number carries its own country);
 * otherwise the number is parsed as a national number of countryCode.
 * @param {string} input - what the user typed
 * @param {string} [countryCode] - ISO 3166-1 alpha-2, e.g. "IL"
 * @returns {string|null} E.164 (e.g. "+972520000000") or null when it cannot be parsed
 */
export const normalizePhone = (input, countryCode) => {
  if (typeof input !== 'string') return null;
  const value = input.trim();
  if (!value) return null;
  try {
    const parsed = value.startsWith('+')
      ? parsePhoneNumber(value)
      : parsePhoneNumber(value, countryCode || undefined);
    return parsed && parsed.isValid() ? parsed.number : null;
  } catch (error) {
    return null;
  }
};

/**
 * The national way of writing an E.164 number, e.g. "+972525457077" -> "052-545-7077".
 * Used for the created contact's name so the chip reads "052-545-7077 (+972525457077)".
 * @param {string} e164
 * @returns {string|null} null when the input is not a parseable number
 */
export const formatNationalPhone = e164 => {
  if (typeof e164 !== 'string' || !e164.startsWith('+')) return null;
  try {
    const parsed = parsePhoneNumber(e164);
    return parsed ? parsed.formatNational() : null;
  } catch (error) {
    return null;
  }
};
